import { db } from './firebase';
import { 
  doc, getDoc, setDoc, updateDoc, collection, 
  query, orderBy, limit, getDocs, addDoc, serverTimestamp 
} from 'firebase/firestore';
import { earnToWalletA, createTransaction } from './walletService';

// Create a new prediction
export const createPrediction = async (uid, matchId, predictionData) => {
  try {
    const predictionRef = doc(db, 'predictions', matchId, uid, predictionData.type);
    
    await setDoc(predictionRef, {
      ...predictionData,
      timestamp: serverTimestamp(),
      status: 'pending' // pending, correct, incorrect
    });
    
    // Update user's prediction cycle counter
    await updatePredictionCycleCount(uid);
    
    return true;
  } catch (error) {
    console.error("Error creating prediction:", error);
    return false;
  }
};

// Get user predictions for a match
export const getMatchPredictions = async (uid, matchId) => {
  try {
    const predictionsRef = collection(db, 'predictions', matchId, uid);
    const q = query(predictionsRef, orderBy('timestamp', 'desc'));
    
    const querySnapshot = await getDocs(q);
    const predictions = [];
    
    querySnapshot.forEach((doc) => {
      predictions.push({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      });
    });
    
    return predictions;
  } catch (error) {
    console.error("Error getting match predictions:", error);
    return [];
  }
};

// Get prediction cycle data for a user
export const getPredictionCycle = async (uid) => {
  try {
    const cycleRef = doc(db, 'users', uid, 'predictionCycle', 'current');
    const cycleDoc = await getDoc(cycleRef);
    
    if (cycleDoc.exists()) {
      return cycleDoc.data();
    } else {
      // Initialize cycle data if it doesn't exist
      await initializePredictionCycle(uid);
      return { count: 0, correctCount: 0 };
    }
  } catch (error) {
    console.error("Error getting prediction cycle:", error);
    return { count: 0, correctCount: 0 };
  }
};

// Initialize prediction cycle for a new user
export const initializePredictionCycle = async (uid) => {
  const cycleRef = doc(db, 'users', uid, 'predictionCycle', 'current');
  
  try {
    const cycleDoc = await getDoc(cycleRef);
    
    if (!cycleDoc.exists()) {
      await setDoc(cycleRef, {
        count: 0,           // Total predictions in current cycle
        correctCount: 0     // Correct predictions in current cycle
      });
    }
    
    return true;
  } catch (error) {
    console.error("Error initializing prediction cycle:", error);
    return false;
  }
};

// Update prediction count in the cycle
export const updatePredictionCycleCount = async (uid) => {
  try {
    const cycleRef = doc(db, 'users', uid, 'predictionCycle', 'current');
    const cycleDoc = await getDoc(cycleRef);
    
    if (!cycleDoc.exists()) {
      await initializePredictionCycle(uid);
      return true;
    }
    
    const cycleData = cycleDoc.data();
    const newCount = cycleData.count + 1;
    
    await updateDoc(cycleRef, {
      count: newCount
    });
    
    // Check if the cycle is complete (20 predictions)
    if (newCount >= 20) {
      // Check if qualified for bonus (12+ correct)
      if (cycleData.correctCount >= 12) {
        // Award the bonus
        await awardCycleBonus(uid);
      }
      
      // Reset cycle for next round
      setTimeout(async () => {
        await resetPredictionCycle(uid);
      }, 5000); // Small delay to ensure UI can display completion
    }
    
    return true;
  } catch (error) {
    console.error("Error updating prediction cycle count:", error);
    return false;
  }
};

// Update prediction result (correct/incorrect)
export const updatePredictionResult = async (uid, matchId, predictionType, isCorrect) => {
  try {
    // Update the prediction status
    const predictionRef = doc(db, 'predictions', matchId, uid, predictionType);
    await updateDoc(predictionRef, {
      status: isCorrect ? 'correct' : 'incorrect'
    });
    
    // Update user's correct prediction count in the cycle
    if (isCorrect) {
      await incrementCorrectPredictionCount(uid);
    }
    
    return true;
  } catch (error) {
    console.error("Error updating prediction result:", error);
    return false;
  }
};

// Increment correct prediction count
export const incrementCorrectPredictionCount = async (uid) => {
  try {
    const cycleRef = doc(db, 'users', uid, 'predictionCycle', 'current');
    const cycleDoc = await getDoc(cycleRef);
    
    if (!cycleDoc.exists()) {
      await initializePredictionCycle(uid);
      return true;
    }
    
    const cycleData = cycleDoc.data();
    
    await updateDoc(cycleRef, {
      correctCount: cycleData.correctCount + 1
    });
    
    return true;
  } catch (error) {
    console.error("Error incrementing correct prediction count:", error);
    return false;
  }
};

// Reset prediction cycle
export const resetPredictionCycle = async (uid) => {
  try {
    const cycleRef = doc(db, 'users', uid, 'predictionCycle', 'current');
    
    await updateDoc(cycleRef, {
      count: 0,
      correctCount: 0
    });
    
    return true;
  } catch (error) {
    console.error("Error resetting prediction cycle:", error);
    return false;
  }
};

// Award bonus for completing a successful cycle (12+ correct predictions out of 20)
export const awardCycleBonus = async (uid) => {
  const BONUS_AMOUNT = 150000; // 1,50,000 coins
  
  try {
    // Add bonus to Wallet A
    await earnToWalletA(uid, BONUS_AMOUNT, 'accuracyBonus');
    
    // Create a special bonus transaction record
    await createTransaction(uid, 'bonus', BONUS_AMOUNT, 'accuracyBonus');
    
    // Log the bonus award for analytics
    const bonusLogRef = collection(db, 'users', uid, 'bonusLog');
    await addDoc(bonusLogRef, {
      amount: BONUS_AMOUNT,
      timestamp: serverTimestamp(),
      reason: 'prediction_cycle_bonus'
    });
    
    return true;
  } catch (error) {
    console.error("Error awarding cycle bonus:", error);
    return false;
  }
};

// Get bonus amount for the prediction cycle
export const getAccuracyBonusAmount = () => {
  return 150000; // 1.5 Lakh coins fixed bonus
}; 